import { formatDistanceToNow } from "date-fns";

// Conductor returns full enum names (e.g. "PLAN_MODE_RUN_ALL"); the admin UI
// shows the short form ("RUN_ALL").
export const displayPlanMode = (mode: string): string => mode.replace(/^PLAN_MODE_/, "");

// "RUN_ALL" -> "PLAN_MODE_RUN_ALL" (the conductor enum name the API wants)
export const fullPlanMode = (mode: "RUN_ALL" | "RUN_ONE"): string => `PLAN_MODE_${mode}`;

export const formatRelativeTime = (iso: string | null): string => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return formatDistanceToNow(date, { addSuffix: true });
};

// "PLAN_STEP_TYPE_BLUEPRINT" -> "BLUEPRINT"
export const displayStepType = (type: string): string => type.replace(/^PLAN_STEP_TYPE_/, "");

// "BLUEPRINT" -> "PLAN_STEP_TYPE_BLUEPRINT" (the conductor enum name the API wants)
export const fullStepType = (kind: "BLUEPRINT" | "PLAN"): string => `PLAN_STEP_TYPE_${kind}`;

// Sync types a step's eligibility can filter on.
export const SYNC_TYPE_OPTIONS = ["FULL", "SUBSEQUENT"];

// Mirrors conductor's windowSyncStepAllowsFullSync: a window sync step's
// eligibility can't be restricted to Subsequent only.
export const windowSyncStepAllowsFullSync = (rules: { sync_types: string[] }[]): boolean =>
  rules.length === 0 ||
  rules.some((rule) => rule.sync_types.length === 0 || rule.sync_types.includes("FULL"));

// Humanize a playbook entry's source_type. A null/empty source means the entry
// was authored directly in the admin tool (no upstream source).
export const displaySourceType = (sourceType: string | null): string => {
  if (!sourceType) return "None";
  switch (sourceType) {
    case "PLAYBOOK_ENTRY_SOURCE_TYPE_SYNC_PLAN_CONFIGURATION":
      return "Sync plan config";
    case "PLAYBOOK_ENTRY_SOURCE_TYPE_LINKED_ACCOUNT_BLUEPRINT_SCHEDULE":
      return "LA blueprint schedule";
    case "PLAYBOOK_ENTRY_SOURCE_TYPE_LINKED_ACCOUNT_SLOW_BLUEPRINT_SCHEDULE":
      return "LA slow blueprint";
    default:
      return sourceType.replace(/^PLAYBOOK_ENTRY_SOURCE_TYPE_/, "");
  }
};

const formatMinutes = (minutes: number): string => {
  if (minutes % (60 * 24) === 0) return `${minutes / 1440}d`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${minutes}m`;
};

export const formatCadence = (minutes: number): string => `every ${formatMinutes(minutes)}`;

// A playbook's resolution specificity, from which scope fields are set.
export const specificityLabel = (
  organizationId: string | null,
  linkedAccountId: string | null
): string => {
  if (linkedAccountId) return "LA-specific";
  if (organizationId) return "Org-specific";
  return "Integration default";
};

export interface EligibilityPart {
  label: string;
  value: string;
}

// Breaks an eligibility ruleset into an OR-list of rules, each an AND-list of
// labeled parts. An empty result means the step is always eligible.
export const summarizeEligibility = (
  rules: {
    sync_types: string[];
    min_minutes_since_last_attempt: number | null;
    min_minutes_since_last_success: number | null;
    if_last_attempt_failed: boolean | null;
  }[]
): EligibilityPart[][] => {
  return (rules || [])
    .map((rule) => {
      const parts: EligibilityPart[] = [];
      if (rule.sync_types.length)
        parts.push({ label: "sync type", value: rule.sync_types.join(" | ") });
      if (rule.min_minutes_since_last_attempt) {
        parts.push({
          label: "since last attempt",
          value: `≥ ${formatMinutes(rule.min_minutes_since_last_attempt)}`,
        });
      }
      if (rule.min_minutes_since_last_success) {
        parts.push({
          label: "since last success",
          value: `≥ ${formatMinutes(rule.min_minutes_since_last_success)}`,
        });
      }
      if (rule.if_last_attempt_failed)
        parts.push({ label: "only if", value: "last attempt failed" });
      return parts;
    })
    .filter((parts) => parts.length > 0);
};
